import { DateTime } from "luxon";
import emailTemplatesService from "@/services/emailTemplatesService";

const state = () => ({
    all: [],
    single: null
});

const actions = {
    async findAll({ commit, dispatch }) {
        try {
            const res = await emailTemplatesService.findAll();
            commit("setAll", res.data);
            return Promise.resolve(JSON.parse(JSON.stringify(res.data)));
        } catch (error) {
            dispatch(
                "common/showError",
                "E-Mail Templates konnten nicht geladen werden.",
                {
                    root: true
                }
            );
            return Promise.reject();
        }
    },
    async update({ dispatch }, formData) {
        try {
            await emailTemplatesService.update(formData);
            dispatch("findAll");
            dispatch(
                "common/showSuccess",
                "E-Mail Template wurde erfolgreich aktualisiert.",
                { root: true }
            );
            return Promise.resolve();
        } catch (error) {
            dispatch(
                "common/showError",
                "E-Mail Template konnte nicht aktualisiert werden.",
                { root: true }
            );
            return Promise.reject();
        }
    },
    replaceTemplateValues({ rootState }, data) {
        data.template.subject = replaceValues(data.template.subject, {
            ...data.booking,
            user: rootState.auth.user
        });
        data.template.body = replaceValues(data.template.body, {
            ...data.booking,
            user: rootState.auth.user
        });
        return data;
    }
};

const getters = {};

const mutations = {
    setAll(state, object) {
        state.all = object;
    },
    setSingle(state, object) {
        state.single = object;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};

function replaceValues(template, val) {
    const dateOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    };

    template = template.replace(
        /{{Datum Heute}}/g,
        new Date().toLocaleDateString("de-DE", dateOptions)
    );
    if (!val) return template;

    if (val.owner && val.owner.month)
        template = template.replace(
            /{{Monat}}/g,
            monthNames[val.owner.month]
        );
    if (val.owner && val.owner.year)
        template = template.replace(
            /{{Jahr}}/g,
            val.owner.year
        );
    if (val.startDate)
        template = template.replace(
            /{{Anreise}}/g,
            new Date(val.startDate).toLocaleDateString("de-DE", dateOptions)
        );
    if (val.endDate)
        template = template.replace(
            /{{Abreise}}/g,
            new Date(val.endDate).toLocaleDateString("de-DE", dateOptions)
        );
    if (val.startDate && val.endDate)
        template = template.replace(
            /{{Übernachtungen}}/g,
            Math.ceil(
                DateTime.fromISO(val.endDate)
                    .diff(DateTime.fromISO(val.startDate), ["days"])
                    .toObject().days
            )
        );
    if (val.adults !== undefined)
        template = template.replace(/{{Erwachsene}}/g, val.adults);
    if (val.children !== undefined)
        template = template.replace(/{{Kinder}}/g, val.children);

    if (val.customer) {
        if (val.customer.salutation)
            template = template.replace(
                /{{Gast Begrüßung}}/g,
                `Sehr ${val.customer.salutation === "Frau" ? "geehrte" : "geehrter"} ${val.customer.salutation
                } ${val.customer.title ? val.customer.title + ' ' : ''}${val.customer.lastName}`
            );
        if (val.customer.salutation)
            template = template.replace(/{{Gast Anrede}}/g, val.customer.salutation);
        if (val.customer.firstName)
            template = template.replace(/{{Gast Vorname}}/g, val.customer.firstName);
        if (val.customer.lastName)
            template = template.replace(/{{Gast Nachname}}/g, val.customer.lastName);
    }

    if (val.rentObject) {
        if (val.rentObject.name)
            template = template.replace(/{{Objekt Name}}/g, val.rentObject.name);
        if (val.rentObject.code)
            template = template.replace(/{{Objekt Code}}/g, val.rentObject.code);
        if (val.rentObject.street)
            template = template.replace(/{{Objekt Straße}}/g, val.rentObject.street);
        if (val.rentObject.zip)
            template = template.replace(/{{Objekt PLZ}}/g, val.rentObject.zip);
        if (val.rentObject.city)
            template = template.replace(/{{Objekt Stadt}}/g, val.rentObject.city);
        if (val.rentObject.url && val.agencyPortalUrl)
            template = template.replace(
                /{{Objekt URL}}/g,
                `https://www.${val.agencyPortalUrl}/ferienwohnung/${val.rentObject.url}`
            );
    }

    if (val.rentObject && val.rentObject.owner) {
        const ownerName = generateOwnerName(val.rentObject.owner);
        template = template.replace(
            /{{Eigentümer Begrüßung}}/g,
            ownerName
        );
    }

    if (val.parentContractId) {
        template = template.replace(
            /{{Mietvertragsnummer}}/g,
            val.parentContractId
        );
    }

    if (val.offers && val.offers.length > 0) {
        template = template.replace(
            /{{Angebotsnummer}}/g,
            val.offers[val.offers.length - 1].id
        );
    }

    if (val.contractBackDays) {
        template = template.replace(
            /{{Datum Vertrag zurück}}/g,
            new Date(val.contractBackDays).toLocaleDateString("de-DE", dateOptions)
        );
    }

    if (val.cancelledAt) {
        template = template.replace(
            /{{Datum Stornierung}}/g,
            new Date(val.cancelledAt).toLocaleDateString("de-DE", dateOptions)
        );
    }

    if (val.pdfmakeTermsAndConditions) {
        template = template.replace(
            /{{Geschäftsbedingungen}}/g,
            val.pdfmakeTermsAndConditions
        );
    }

    if (val.totalPrice) {
        template = template.replace(
            /{{Gesamtmiete}}/g,
            toCurrency(val.totalPrice / 100)
        );
    }

    if (val.totalRentPrice) {
        template = template.replace(
            /{{Objektmiete}}/g,
            toCurrency(val.totalRentPrice / 100)
        );
    }

    if (val.totalItemPrice) {
        template = template.replace(
            /{{Leistungspreis}}/g,
            toCurrency(val.totalItemPrice / 100)
        );
    }

    if (val.user) {
        template = template.replace(/{{Benutzer Vorname}}/g, val.user.firstName);
        template = template.replace(/{{Benutzer Nachname}}/g, val.user.lastName);
    }

    if (val.finalPaymentDueDate) {
        template = template.replace(
            /{{Restzahlungsbetrag}}/g,
            `${toCurrency((val.totalFinalPayment) / 100)}`
        );
        template = template.replace(
            /{{Restzahlungsfälligkeit}}/g,
            new Date(val.finalPaymentDueDate).toLocaleDateString('de-DE', dateOptions)
        );
        template = template.replace(
            /{{Zahlungen}}/g,
            toCurrency((val.totalPayments) / 100)
        );
        template = template.replace(
            /{{Zahlungsfälligkeit}}/g,
            new Date(val.paymentDueDate).toLocaleDateString('de-DE', dateOptions)
        );
        template = template.replace(
            /{{Mahnungsfälligkeit}}/g,
            new Date(val.dueDate).toLocaleDateString('de-DE', dateOptions)
        );
        template = template.replace(
            /{{Mahnungsbetrag}}/g,
            toCurrency((val.totalPaymentDue - val.totalPayments) / 100)
        );
    }

    if (val.downPaymentDueDate) {
        template = template.replace(
            /{{Anzahlungsbetrag}}/g,
            `${toCurrency((val.totalDownPayment) / 100)}`
        );
        template = template.replace(
            /{{Anzahlungsfälligkeit}}/g,
            new Date(val.downPaymentDueDate).toLocaleDateString('de-DE', dateOptions)
        );
        template = template.replace(
            /{{WENN#Anzahlungsbetrag}}/g,
            ``
        );
        template = template.replace(
            /{{Anzahlungsbetrag#WENN}}/g,
            ``
        );
    } else {
        template = template.replace(
            /{{WENN#Anzahlungsbetrag}}.*{{Anzahlungsbetrag#WENN}}/g,
            ``
        );
    }

    if (val.owner) {
        const ownerName = generateOwnerName(val.owner);
        template = template.replace(
            /{{Eigentümer Begrüßung}}/g,
            ownerName
        );
    }

    return template;
}

function toCurrency(value) {
    if (typeof value !== "number") {
        return value;
    }
    var formatter = new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2
    });
    return formatter.format(value);
}

export function generateOwnerName(data) {
    let names = [];

    if (data.ownerOneSalutation && data.ownerOneLastName) {
        names.push(`sehr ${data.ownerOneSalutation === "Frau" ? "geehrte" : "geehrter"} ${data.ownerOneSalutation}${data.ownerOneTitle ? ' ' + data.ownerOneTitle : ''} ${data.ownerOneLastName}`);
    }

    if (data.ownerTwoSalutation && data.ownerTwoLastName) {
        const ownerName = `sehr ${data.ownerTwoSalutation === "Frau" ? "geehrte" : "geehrter"} ${data.ownerTwoSalutation}${data.ownerTwoTitle ? ' ' + data.ownerTwoTitle : ''} ${data.ownerTwoLastName}`;
        if (data.ownerTwoSalutation === "Frau") {
            names.unshift(ownerName);
        } else {
            names.push(ownerName);
        }
    }

    if (data.ownerThreeSalutation && data.ownerThreeLastName) {
        const ownerName = `sehr ${data.ownerThreeSalutation === "Frau" ? "geehrte" : "geehrter"} ${data.ownerThreeSalutation}${data.ownerThreeTitle ? ' ' + data.ownerThreeTitle : ''} ${data.ownerThreeLastName}`;
        if (data.ownerThreeSalutation === "Frau") {
            names.unshift(ownerName);
        } else {
            names.push(ownerName);
        }
    }

    const joinedString = 'S' + names.join(',\n').substring(1);
    return joinedString;
}

const monthNames = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
]